import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Divider,
  Grid2 as Grid,
  Typography,
  CardMedia,
  IconButton,
  Stack,
  Avatar,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { formatNumberWithSuffix } from "../../../../utils/functions";
import ytengagement from "../../../Assets/Img/yt engagement img.svg";
import ytshort from "../../../Assets/Img/yt short engagement img.svg";
import FollowersGrowthChart from "./FollowerGrowthChart";
import LikesGrowthChart from "./LikesGrowthChart";
import CustomPieChart from "../../../Common/Components/Charts/PieChart";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import lv from "../../../Assets/Img/lv.svg";

const formatDate = (isoDate) => {

  if (!isoDate) return "";

  const posted = new Date(isoDate);
  const day = posted.getDate();
  const month = posted
    .toLocaleString("default", { month: "short" })
    .toUpperCase(); // Get the short month name (e.g., "APR")
  const year = posted.getFullYear().toString().slice(-2); // Get the last two digits of the year

  return `${day} ${month} ${year}`;
};

// The function to fetch YouTube stats remains unchanged
const getYouTubeStats = (stats) => {
  return [
    {
      label: "Subscribers",
      value: stats?.subscribers
        ? formatNumberWithSuffix(stats?.subscribers)
        : 0,
    },
    {
      label: "AVG Views",
      value: stats?.total_views
        ? formatNumberWithSuffix(stats?.total_views)
        : 0,
    },
    {
      label: "ER",
      value: stats?.engagement ? stats?.engagement + " %" : "N/A",
      value: stats?.engagement ? stats?.engagement + "%" : "N/A",
    },
    {
      label: "Likes",
      value: stats?.total_likes ? formatNumberWithSuffix(stats?.avg_likes) : 0,
    },
    {
      label: "AVG Comment",
      value: stats?.total_comments
        ? formatNumberWithSuffix(stats?.avg_comments)
        : 0,
    },
    {
      label: "Total Videos",
      value: stats?.total_videos
        ? formatNumberWithSuffix(stats?.total_videos)
        : "N/A",
    },
  ];
};
const getEngagementYouTube = (stats) => {
  return [
    {
      label: "AVG. VIEWS",
      value: stats?.total_views ? formatNumberWithSuffix(stats?.avg_views) : 0,
    },
    {
      label: "AVG. LIKES",
      value: stats?.total_likes ? formatNumberWithSuffix(stats?.avg_likes) : 0,
    },
    {
      label: "AVG. COMMENTS",
      value: stats?.total_comments
        ? formatNumberWithSuffix(stats?.avg_comments)
        : 0,
    },
  ];
};
const getRecentVideos = (stats) => {
  return (
    stats?.latest_videos?.slice(0, 4).map((video, index) => {
      const videoId = extractYouTubeId(video.video_link);
      return {
        id: index,
        title: video.video_title || `Video ${index + 1}`,
        thumbnail: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
        video_link: video.video_link,
        videoId,
        views: formatNumberWithSuffix(video.views || 0),
        likes: formatNumberWithSuffix(video.likes || 0),
        comments: formatNumberWithSuffix(video.comments || 0),
        viewsDisplay: formatNumberWithSuffix(video.views || 0),
        date: formatDate(video.post_date),
      };
    }) || []
  );
};
const getMostPopularVideos = (stats) => {
  return (
    stats?.most_liked_videos?.slice(0, 4).map((video, index) => {
      const videoId = extractYouTubeId(video.video_link);
      return {
        id: index,
        title: video.video_title || `Video ${index + 1}`,
        thumbnail: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
        video_link: video.video_link,
        videoId,
        views: formatNumberWithSuffix(video.views || 0),
        likes: formatNumberWithSuffix(video.likes || 0),
        comments: formatNumberWithSuffix(video.comments || 0),
        viewsDisplay: formatNumberWithSuffix(video.views || 0),
        date: formatDate(video.post_date),
      };
    }) || []
  );
};

// Helper to extract YouTube video ID from URL
const extractYouTubeId = (url) => {
  const match = url.match(/v=([a-zA-Z0-9_-]+)/);
  return match ? match[1] : "";
};

// Sample data for Platforms (YouTube stats)
const data = [
  {
    type: "Videos",
    icon: (
      <img src={ytengagement} alt="Videos" style={{ width: 25, height: 25 }} />
    ),
    stats: [
      { label: "AVG. VIEWS", value: "294.4k" },
      { label: "AVG. LIKES", value: "22.3k" },
      { label: "AVG. COMMENTS", value: "1.9k" },
    ],
  },
  {
    type: "Shorts",
    icon: <img src={ytshort} alt="Shorts" style={{ width: 25, height: 25 }} />,
    stats: [
      { label: "AVG. VIEWS", value: "66.2k" },
      { label: "AVG. LIKES", value: "13k" },
      { label: "AVG. COMMENTS", value: "344" },
    ],
  },
];

const ages = (data = []) => {
  return data && Array.isArray(data)
    ? data.map((i) => {
      return {
        label: i.name.replace("_", "-"), // Replace underscore with hyphen for age range
        value: i.percent.toFixed(2),
      };
    })
    : [];
};
const genderData = (data) => {
  // Default to 0 if values are not available
  const malePercent = data?.male || 0;
  const femalePercent = data?.female || 0;

  // Calculate other gender categories (if needed)
  const otherPercent = 100 - malePercent - femalePercent;

  return [
    {
      name: "Male",
      value: malePercent,
      fill: "rgba(107, 142, 118, 1)", // Male color
    },
    {
      name: "Female",
      value: femalePercent,
      fill: "rgba(182, 215, 185, 1)", // Female color
    },
    {
      name: "Other",
      value: otherPercent > 0 ? otherPercent : 0, // Ensures no negative percentages
      fill: "rgba(151, 183, 154, 1)", // Other color
    },
  ];
};

// Main Platform component
export const Platforms = ({ details }) => {

  // collaboration with data
  const [CollaborateWith, setCollaborateWith] = useState(
    details?.pastCollaboratedBrandData || []
  );

  useEffect(() => {
    setCollaborateWith(details?.pastCollaboratedBrandData || []);
  }, [details]);

  const statsOverview = getYouTubeStats({
    ...(details.youtube_stats || {}),
    // engagement: details?.engagement || 0,
  });
  const engagementViews = getEngagementYouTube({
    ...(details?.youtube_stats?.regular_video_stats || {}),
    engagement: details?.engagement || 0,
  });
  const recentVideos = getRecentVideos({
    ...(details.youtube_stats || {}),
    engagement: details?.engagement || 0,
  });
  const mostPopularVideos = getMostPopularVideos({
    ...(details.youtube_stats || {}),
    engagement: details?.engagement || 0,
  });

  const collaborators = [
    {
      name: "Nykaa",
      img: lv,
    },
    {
      name: "John Morgan",
      img: "https://randomuser.me/api/portraits/men/32.jpg",
    },
    {
      name: "Chanel",
      img: lv,
    },
    {
      name: "Gucci",
      img: "https://upload.wikimedia.org/wikipedia/commons/3/38/Gucci_Logo.svg",
    },
    {
      name: "Louis Vuitton",
      img: "https://1000logos.net/wp-content/uploads/2017/06/Louis-Vuitton-Logo.png",
    },
    {
      name: "Alex Volkov",
      img: lv,
    },
    {
      name: "Kiara Sharma",
      img: "https://randomuser.me/api/portraits/women/44.jpg",
    },
    {
      name: "Rhys Larsen",
      img: lv,
    },
    {
      name: "H&M",
      img: "https://upload.wikimedia.org/wikipedia/commons/5/53/H%26M-Logo.svg",
    },
  ];

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // const recentVideos = getRecentVideos(details.youtube_stats);

  return (
    <Box>
      <Grid container spacing={4}>
        <Grid size={12}>
          <Box
            sx={{
              border: "1px solid rgba(238, 238, 238, 1)",
              borderRadius: "4px",
              p: 3,
            }}
          >
            <Typography
              className="heading text-start"
              sx={{
                color: "rgba(0, 0, 0, 1)",
                fontWeight: "500 !important",
                fontSize: "20px",
                fontFamily: "Poppins, sans-serif",
              }}
            >
              Overview
            </Typography>
            <Grid container spacing={2}>
              {statsOverview.map((stat, index) => {
                return (
                  <Grid key={index} size={2}>
                    <Box
                      display={"flex"}
                      alignItems={"center"}
                      flexDirection={"column"}
                      sx={{
                        padding: "1rem",
                        borderRadius: "8px",
                        border: "1px solid rgba(238, 238, 238, 1)",
                      }}
                    >
                      <Typography
                        fontSize={"18px"}
                        fontWeight={600}
                        color="#676767"
                      >
                        {stat.value}
                      </Typography>
                      <Typography color="#676767" fontSize={"14px"}>
                        {stat.label}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Grid>
      </Grid>
      {/* Engagement & Views Section */}
      <Grid>
        <Box
          sx={{
            border: "1px solid rgba(238, 238, 238, 1)",
            borderRadius: "4px",
            p: 3,

            mt: 2,
          }}
        >
          <Typography className="heading text-start" fontWeight="bold" mb={2}>
            Engagement & Views
          </Typography>

          {data.map((item, index) => (
            <React.Fragment key={item.type}>
              <Box sx={{ display: "flex", my: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    width: "100px",
                  }}
                >
                  {item.icon}
                  <Typography
                    variant="body1"
                    fontWeight="medium"
                    mt={1} // add margin-top to separate from icon
                  >
                    {item.type}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flex: 0.5,
                    justifyContent: "space-evenly",
                  }}
                >
                  {item.stats.map((stat, statIdx) => (
                    <Box key={statIdx} sx={{ textAlign: "start", px: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.7rem", letterSpacing: 0.5 }}
                      >
                        {stat.label}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {/* Dynamically set value from stats */}
                        {index === 0
                          ? engagementViews[statIdx]?.value // for 'Videos'
                          : // : engagementViews[statIdx]?.value}{" "}
                          " N/A"}
                        {/* for 'Shorts' */}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
              {index < data.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Box>
      </Grid>
      {/* chart */}
      <Grid>
        <FollowersGrowthChart />
      </Grid>
      <Grid>
        <LikesGrowthChart />
      </Grid>
      {/* 
            Followers Details
       */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          border: "1px solid rgba(238, 238, 238, 1)",
          borderRadius: "8px",
          mt: "10px",
          padding: "10px",
          mb: 1,
        }}
      >
        <Grid size={12}>
          <Box>
            <Typography className="heading text-start">
              Audience Details By Subscribers{" "}
            </Typography>
            <Grid container spacing={4} sx={{ height: "100%" }}>
              <Grid size={6}>
                <Box
                  sx={{
                    background: "#FFFFFF",
                    padding: "1rem 1rem 2rem",
                    borderRadius: "8px",
                    height: "100%",
                    border: "1px solid rgba(238, 238, 238, 1)",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#676767",
                      "& span": {
                        fontSize: "0.6rem",
                        fontWeight: 400,
                      },
                    }}
                  >
                    Top Countries
                  </Typography>
                  <Box mt={2} display={"flex"} flexDirection={"column"} gap={3}>
                    {details?.instagram_stats?.top_countries.length > 0 ? (
                      details?.instagram_stats?.top_countries.map((i) => {
                        return (
                          <Box>
                            <Box
                              display={"flex"}
                              justifyContent={"space-between"}
                            >
                              <Typography fontSize={"14px"}>
                                {i.name}
                              </Typography>
                              <Typography fontSize={"14px"}>
                                {i.percent?.toFixed(2)} %
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                height: "8px",
                                borderRadius: "4px",
                                background: "#67676733",
                                marginTop: "6px",
                              }}
                            >
                              <Box
                                sx={{
                                  width: `${i.percent}%`,
                                  height: "8px",
                                  borderRadius: "4px",
                                  background: "#6B8E76",
                                }}
                              />
                            </Box>
                          </Box>
                        );
                      })
                    ) : (
                      <Box
                        sx={{
                          minHeight: "240px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        No Data Available
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
              <Grid size={6}>
                <Box
                  sx={{
                    background: "#FFFFFF",
                    padding: "1rem 1rem 2rem",
                    borderRadius: "8px",
                    height: "100%",
                    border: "1px solid rgba(238, 238, 238, 1)",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#676767",
                      "& span": {
                        fontSize: "0.6rem",
                        fontWeight: 400,
                      },
                    }}
                  >
                    Top Cities{" "}
                  </Typography>
                  <Box mt={2} display={"flex"} flexDirection={"column"} gap={3}>
                    {details.instagram_stats?.top_cities?.length > 0 ? (
                      details.instagram_stats?.top_cities.map((i) => {
                        return (
                          <Box>
                            <Box
                              display={"flex"}
                              justifyContent={"space-between"}
                            >
                              <Typography fontSize={"14px"}>
                                {i.name}
                              </Typography>
                              <Typography fontSize={"14px"}>
                                {i.percent.toFixed(2)} %
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                height: "8px",
                                borderRadius: "4px",
                                background: "#67676733",
                                marginTop: "6px",
                              }}
                            >
                              <Box
                                sx={{
                                  width: `${i.percent}%`,
                                  height: "8px",
                                  borderRadius: "4px",
                                  background: "#6B8E76",
                                }}
                              />
                            </Box>
                          </Box>
                        );
                      })
                    ) : (
                      <Box
                        sx={{
                          minHeight: "240px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        No Data Available
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
              <Grid size={6}>
                <Box
                  sx={{
                    background: "#FFFFFF",
                    padding: "1rem 1rem 2rem",
                    borderRadius: "8px",
                    height: "100%",
                    border: "1px solid rgba(238, 238, 238, 1)",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: "#676767",
                      "& span": {
                        fontSize: "0.6rem",
                        fontWeight: 400,
                      },
                    }}
                  >
                    Age
                  </Typography>
                  <Box
                    mt={2}
                    display={"flex"}
                    flexDirection={"column"}
                    gap={3}
                    height={"100%"}
                  >
                    {ages(details?.instagram_stats?.age_groups || []).length >
                      0 ? (
                      ages(details?.instagram_stats?.age_groups || []).map(
                        (i) => {
                          return (
                            <Box>
                              <Box
                                display={"flex"}
                                justifyContent={"space-between"}
                              >
                                <Typography fontSize={"14px"}>
                                  {i.label}
                                </Typography>
                                <Typography fontSize={"14px"}>
                                  {i.value} %
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  height: "8px",
                                  borderRadius: "4px",
                                  background: "#67676733",
                                  marginTop: "6px",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${i.value}%`,
                                    height: "8px",
                                    borderRadius: "4px",
                                    background: "#6B8E76",
                                  }}
                                />
                              </Box>
                            </Box>
                          );
                        }
                      )
                    ) : (
                      <Box
                        sx={{
                          minHeight: "240px",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        No Data Available
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
              {/* Gender */}

              <Grid size={6}>
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  gap={2}
                  height={"100%"}
                >
                  <Box
                    height={"100%"}
                    sx={{
                      border: "1px solid rgba(238, 238, 238, 1)",
                      p: 2,
                      borderRadius: "4px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#676767",
                        "& span": {
                          fontSize: "0.6rem",
                          fontWeight: 400,
                        },
                      }}
                    >
                      Gender
                    </Typography>

                    {(() => {
                      const gender = genderData(details); // Use the details object directly
                      const isGenderDataExists =
                        gender.filter((i) => i.value !== 0).length > 0;

                      return (
                        <Box
                          sx={{
                            background: "#fff",
                            display: "flex",
                            alignItems: "stretch",
                            flexDirection: "column",
                            borderRadius: "8px",
                            padding: "1rem",
                            gap: "1rem",
                          }}
                        >
                          {isGenderDataExists ? (
                            <>
                              <Box display="flex" justifyContent="center">
                                <CustomPieChart
                                  width={200}
                                  height={200}
                                  data={gender}
                                  innerRadius={60}
                                  customLabel={false}
                                />
                              </Box>

                              <Box
                                display="flex"
                                flexDirection="column"
                                gap={1}
                                width="100%"
                              // maxWidth="300px"
                              >
                                {gender.map((i) => (
                                  <Box
                                    key={i.name}
                                    display="flex"
                                    alignItems="center"
                                    gap={3}
                                    color="#6B4750"
                                    px={2}
                                    py={1}
                                    sx={{
                                      border: "1px solid #e0e0e0",
                                      borderRadius: "50px",
                                    }}
                                  >
                                    <Box
                                      display="flex"
                                      gap={2}
                                      alignItems="center"

                                    // flexGrow={1}
                                    >
                                      <Box
                                        width="16px"
                                        height="16px"
                                        sx={{
                                          background: i.fill,
                                          borderRadius: "50%",
                                        }}
                                      />
                                      <Typography fontSize="12px">
                                        {i.name} -
                                      </Typography>
                                    </Box>

                                    <Typography
                                      fontSize="12px"
                                      sx={{ fontWeight: "bold" }}
                                    >
                                      <Box fontWeight="bold">
                                        {i.value.toFixed(2)}%
                                      </Box>
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </>
                          ) : (
                            <Box
                              sx={{
                                width: "100%",
                                minHeight: "240px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              No Data Available
                            </Box>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Box>

      <Box
        sx={{
          borderRadius: 2,
          p: 2,
          border: "1px solid rgba(238, 238, 238, 1)",
          width: "100%",
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Collaborate With
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: { xs: 3, sm: 4 },
              justifyContent: { xs: "center", sm: "space-between" },
            }}
          >
            {CollaborateWith.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: { xs: 3, sm: 4 },
                  justifyContent: { xs: "center", sm: "space-between" },
                }}
              >
                {CollaborateWith.map((item, idx) => (
                  <Stack
                    key={idx}
                    spacing={1}
                    alignItems="center"
                    sx={{ width: 80, textAlign: "center" }}
                  >
                    <Avatar
                      alt={item.brand_name || "Unknown Brand"}
                      src={item.profile || lv}
                      sx={{ width: 50, height: 50, bgcolor: "#f5f5f5" }}
                      variant="circular"
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: 12,
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        maxWidth: "100%",
                      }}
                    >
                      {item.brand_name || "N/A"}
                    </Typography>
                  </Stack>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  minHeight: "100px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No Collaborations Available
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Box>

      {/*   videos */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          border: "1px solid rgba(238, 238, 238, 1)",
          borderRadius: "8px",
          mt: "10px",
          padding: "10px",
        }}
      >
        {/* recent videos */}
        <Box
          sx={{
            width: "100%",
            border: "1px solid rgba(238, 238, 238, 1)",
            p: 1,
            borderRadius: "8px",
            mt: "10px",
          }}
        >
          <Typography className="heading text-start mb-2">
            Recent Videos
          </Typography>

          <Grid container spacing={0.5}>
            {recentVideos.slice(0, 4).map((video) => (
              <Grid item xs={12} sm={6} md={3} key={video.id}>
                <Card
                  sx={{
                    borderRadius: 1,
                    boxShadow: "none",
                    height: "100%",
                    width: "100%",
                    maxWidth: "240px",
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: "transparent",
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    <CardMedia
                      component="img"
                      height="180"
                      image={video?.thumbnail}
                      alt={video?.title}
                      sx={{
                        borderRadius: 1,
                        filter: "brightness(0.85)",
                      }}
                    />
                    <IconButton
                      sx={{
                        position: "absolute",
                        top: "20px",
                        right: "8px",
                        height: "28px",
                        width: "28px",
                        color: "white",
                        bgcolor: "rgba(0,0,0,0.3)",
                        "&:hover": {
                          bgcolor: "rgba(0,0,0,0.5)",
                        },
                      }}
                      onClick={() => window.open(video.video_link, "_blank")}
                    >
                      <PlayArrowIcon />
                    </IconButton>

                    {video.viewsDisplay && (
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          width: "100%",
                          textAlign: "center",
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "1.2rem",
                          textShadow: "1px 1px 3px rgba(0,0,0,0.7)",
                          p: 1,
                          background:
                            "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                        }}
                      >
                        {/* {video.viewsDisplay} */}
                      </Box>
                    )}
                  </Box>

                  <CardContent sx={{ p: 1, pt: 1.5, pb: "8px !important" }}>
                    <Stack
                      direction="row"
                      spacing={3} // Increased spacing to make elements more spread out
                      alignItems="center"
                      sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.3 }}
                      >
                        {" "}
                        {/* Increased gap */}
                        <VisibilityIcon sx={{ fontSize: "0.875rem" }} />
                        <Typography variant="caption">
                          {video?.views}
                        </Typography>
                      </Box>

                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.3 }}
                      >
                        {" "}
                        {/* Increased gap */}
                        <ThumbUpAltIcon sx={{ fontSize: "0.875rem" }} />
                        <Typography variant="caption">
                          {video?.likes}
                        </Typography>
                      </Box>

                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.3 }}
                      >
                        {" "}
                        {/* Increased gap */}
                        <ChatBubbleOutlineIcon sx={{ fontSize: "0.875rem" }} />
                        <Typography variant="caption">
                          {video?.comments}
                        </Typography>
                      </Box>

                      <Typography
                        variant="caption"
                        sx={{ ml: "auto !important", color: "text.secondary" }}
                      >
                        {video.date}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/*  most popular videos */}
        <Box
          sx={{
            width: "100%",
            maxWidth: "100%",
            margin: "0",
            border: "1px solid rgba(238, 238, 238, 1)",
            p: 1,
            borderRadius: "8px",
            mt: "10px",
          }}
        >
          <Typography className="heading text-start mb-2">
            Most Popular Videos{" "}
          </Typography>

          <Grid container spacing={0.5}>
            {mostPopularVideos?.slice(0, 4).map((video) => (
              <Grid item xs={12} sm={6} md={3} key={video.id}>
                <Card
                  sx={{
                    borderRadius: 1,
                    boxShadow: "none",
                    height: "100%",
                    width: "100%",
                    maxWidth: "240px",
                    display: "flex",
                    flexDirection: "column",
                    bgcolor: "transparent",
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    <CardMedia
                      component="img"
                      height="180"
                      image={video.thumbnail}
                      alt={video.title}
                      sx={{
                        borderRadius: 1,
                        filter: "brightness(0.85)",
                      }}
                    />
                    <IconButton
                      sx={{
                        position: "absolute",
                        top: "20px",
                        right: "8px",
                        height: "28px",
                        width: "28px",
                        color: "white",
                        bgcolor: "rgba(0,0,0,0.3)",
                        "&:hover": {
                          bgcolor: "rgba(0,0,0,0.5)",
                        },
                      }}
                      onClick={() => window.open(video.video_link, "_blank")}
                    >
                      <PlayArrowIcon />
                    </IconButton>

                    {video.viewsDisplay && (
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 0,
                          width: "100%",
                          textAlign: "center",
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "1.2rem",
                          textShadow: "1px 1px 3px rgba(0,0,0,0.7)",
                          p: 1,
                          background:
                            "linear-gradient(to top, rgba(0,0,0,0.7), transparent)",
                        }}
                      ></Box>
                    )}
                  </Box>

                  <CardContent sx={{ p: 1, pt: 1.5, pb: "8px !important" }}>
                    <Stack
                      direction="row"
                      spacing={""}
                      alignItems="center"
                      sx={{ color: "text.secondary", fontSize: "0.75rem" }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.2 }}
                      >
                        <VisibilityIcon sx={{ fontSize: "0.875rem" }} />
                        <Typography variant="caption">
                          {video?.views}
                        </Typography>
                      </Box>

                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <ThumbUpAltIcon sx={{ fontSize: "0.875rem" }} />
                        <Typography variant="caption">
                          {video?.likes}
                        </Typography>
                      </Box>

                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.2 }}
                      >
                        <ChatBubbleOutlineIcon sx={{ fontSize: "0.875rem" }} />
                        <Typography variant="caption">
                          {video?.comments}
                        </Typography>
                      </Box>

                      <Typography
                        variant="caption"
                        sx={{ ml: "auto !important", color: "text.secondary" }}
                      >
                        {video.date}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};
